import { z, setErrorMap, ZodIssueCode } from 'zod';

// Tamaño máximo en bytes (2 MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024; 
// Hasta un año después del actual para validación de modeloAnio
const CURRENT_YEAR    = new Date().getFullYear() + 1;
// -----------------------------------------------------------------------------
// Override global para campos requeridos y tipos faltantes → "Required"
// -----------------------------------------------------------------------------
setErrorMap((issue, ctx) => {
    if (
        (issue.code === 'invalid_type' && issue.received === 'undefined') ||
        issue.code === 'too_small'
    ) {
        return { message: 'Requerido' };
    }
    return { message: ctx.defaultError };
});

// Expresión regular para permitir solo letras (mayúsculas/minúsculas), acentos y espacios
const onlyLettersRegex = /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+$/;

// -----------------------------------------------------------------------------
// Campos comunes a todos los "terceros"
// -----------------------------------------------------------------------------
const baseSchema = z.object({
    tipoDocumento: z.string().min(1),
    numeroDocumento: z.string().min(1, { message: 'Requerido' }),
    naturaleza: z.string().min(1),
    genero: z.string().min(1),
    direccion: z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Dirección inválida' }),
    pais: z.string().min(1),
    municipio: z.string().min(1),
    celular: z.string().regex(/^\d+$/, { message: 'Solo dígitos' }).min(1, { message: 'Ingrese un celular válido' }).max(10, { message: 'Máximo 10 dígitos' }),
    email: z.string().email(),
    // Información adicional - opcional
    telefonoFijo: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().regex(/^\d+$/).min(7, { message: 'Mínimo 7 dígitos'}).max(10, { message: 'Máximo 10 dígitos'}).optional()
    ),
    fechaNacimiento: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }).optional()
    ),
    lugarNacimiento: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).optional()
    ),
    estadoCivil: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    profesion: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    empresa: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    sitioWeb: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string()
            .regex(
                /^(https?:\/\/)?(www\.)?[-\w@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
                { message: 'URL inválida' }
            )
            .optional()
    ),
    eps: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    arl: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    judicial: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    militar: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    contactoEmergenciaNombre: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    contactoEmergenciaProfesion: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    contactoEmergenciaCelular: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^\d+$/, { message: 'Celular inválido' }).optional()
    ),
    contactoReferenciaNombre: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    contactoReferenciaProfesion: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^[A-Za-z0-9\s.,#-]+$/, { message: 'Datos inválidos' }).optional()
    ),
    contactoReferenciaCelular: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^\d+$/, { message: 'Celular inválido' }).optional()
    )
});

// -----------------------------------------------------------------------------
// Ramas según naturaleza
// -----------------------------------------------------------------------------
const personaNaturalSchema = baseSchema.extend({
    naturaleza: z.literal('N'),
    primerNombre: z.string().min(1, { message: 'Requerido'}).regex(onlyLettersRegex, { message: 'Solo letras' }),
    segundoNombre: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().regex(onlyLettersRegex, { message: 'Solo letras' }).optional()
    ),
    primerApellido: z.string().min(1, { message: 'Requerido'}).regex(onlyLettersRegex, { message: 'Solo letras' }),
    segundoApellido: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().regex(onlyLettersRegex, { message: 'Solo letras' }).optional()
    ),
    razonSocial: z.string().optional(),
    nombreComercial: z.string().optional(),
});

const personaJuridicaSchema = baseSchema.extend({
    naturaleza: z.literal('J'),
    razonSocial: z.string().min(1, { message: 'Requerido'}).regex(/^[A-Za-z0-9\s.-]+$/, { message: 'Datos inválidos' }),
    nombreComercial: z.string().optional(),
    primerNombre: z.string().optional(),
    segundoNombre: z.string().optional(),
    primerApellido: z.string().optional(),
    segundoApellido: z.string().optional(),
});

export const terceroBaseSchema = z.discriminatedUnion('naturaleza', [
    personaNaturalSchema,
    personaJuridicaSchema
]);

// -----------------------------------------------------------------------------
// Mini‐esquema para validar solo Documento en tiempo real (pasos 1,2,3)
// -----------------------------------------------------------------------------
const documentPartial = baseSchema.pick({
    tipoDocumento: true,
    numeroDocumento: true,
    naturaleza: true
});

export const documentoSchema = documentPartial.superRefine((data, ctx) => {
    const { tipoDocumento, numeroDocumento } = data;
    switch (tipoDocumento.toUpperCase()) {
        case 'C':  // Cédula
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Cédula inválida' });
            }
            break;
        case 'DE': // Extranjero
            if (!/^[A-Z0-9]+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Documento extranjero inválido' });
            }
            break;
        case 'E':  // Extranjería
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Extranjería inválida' });
            }
            break;
        case 'N':  // NIT
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'NIT inválido' });
            }
            break;
        case 'NOP': // Nit País
            if (!/^[A-Z0-9]+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Nit Otro País inválido' });
            }
            break;
        case 'P':  // Pasaporte
            if (!/^[A-Z0-9]+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Pasaporte inválido' });
            }
            break;
        case 'PE': // PEP
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'PEP inválido' });
            }
            break;
        case 'PPT':
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'PPT inválido' });
            }
            break;
        case 'R':  // RUT
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'RUT inválido' });
            }
            break;
        case 'RC': // Registro Civil
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Registro Civil inválido' });
            }
            break;
        case 'TE': // Tarjeta Extranjería
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Tarjeta Extranjería inválida' });
            }
            break;
        case 'TI': // Tarjeta Identidad
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'Tarjeta Identidad inválida' });
            }
            break;
        case 'U':  // NUIP
            if (!/^\d+$/.test(numeroDocumento)) {
                ctx.addIssue({ code: ZodIssueCode.custom, path: ['numeroDocumento'], message: 'NUIP inválido' });
            }
            break;
        default:
            ctx.addIssue({ code: ZodIssueCode.custom, path: ['tipoDocumento'], message: 'Tipo de documento no soportado' });
    }
});

// -----------------------------------------------------------------------------
// Esquema de conductor
// -----------------------------------------------------------------------------
export const conductorFields = z.object({
    tipoLicencia: z.string().min(1, { message: 'Seleccione un tipo de licencia' }),
    numeroLicencia: z.string().regex(/^\d+$/, { message: 'Solo dígitos' }).min(5, { message: 'Mínimo 5 dígitos' }),
    fechaVencimientoLicencia: z.string().refine(val => !isNaN(Date.parse(val)), { message: 'Fecha inválida' }),
    anioInicioConductor: z.string().min(1, { message: 'Seleccione un año' }).refine(val => {
        const year = parseInt(val);
        const currentYear = new Date().getFullYear();
        return year >= 1950 && year <= currentYear;
    }, { message: 'Año inválido' })
});

export const conductorSchema = z.discriminatedUnion('naturaleza', [
  personaNaturalSchema.merge(conductorFields),
  personaJuridicaSchema.merge(conductorFields),
]);

// -----------------------------------------------------------------------------
// Esquema de vehículos
// -----------------------------------------------------------------------------
export const vehiculoSchema = z.object({
    placa: z.string().regex(/^[A-Z]{3}\d{3}$/, { message: 'Formato inválido - Debe de ser ABC123' }).min(1, { message: 'Ingrese una Placa Válida' }).max(6, { message: 'Máximo 6 caracteres' }),
    tipoVehiculo: z.string().min(1, { message: 'Seleccione un tipo de vehículo' }),
    marca: z.string().min(1, { message: 'Seleccione una marca' }),
    linea: z.string().min(1, { message: 'Seleccione una línea' }),
    modeloAnio: z.coerce.number().int({ message: 'Año inválido' }).gte(1900, { message: 'Año debe ser mayor o igual a 1900' }).lte(CURRENT_YEAR, { message: 'Año debe ser menor o igual a ' + CURRENT_YEAR }),
    color: z.string().min(1, { message: 'Ingrese un color' }).regex(onlyLettersRegex, { message: 'Solo letras' }),
    carroceria: z.string().min(1, { message: 'Seleccione una carrocería' }),
    numeroEjes: z.coerce.number().int({ message: 'Número de ejes inválido' }).positive({ message: 'Debe ser un número positivo' }),
    tipoCombustible: z.string().min(1, { message: 'Seleccione un tipo de combustible' }),
    pesoVacio: z.coerce.number({ message: 'Peso vacío inválido' }).positive({ message: 'Debe ser un número positivo' }),
    configuracion: z.string().min(1, { message: 'Seleccione una configuracion' }),
    fechaMatricula: z.string().refine(val => !isNaN(Date.parse(val))),
    numeroMotor: z.string().regex(/^[A-Z0-9]+$/, { message: 'Solo letras mayúsculas y dígitos' }).min(1, { message: 'Ingrese el número de motor' }),
    numeroChasis: z.string().regex(/^[A-Z0-9]+$/, { message: 'Solo letras mayúsculas y dígitos' }).min(1, { message: 'Ingrese el número de chasis' }),
    licenciaTransito: z.string().min(1, { message: 'Ingrese la licencia de tránsito' }),
    ciudadOrganismoTransito: z.string().min(1, { message: 'Seleccione una ciudad de organismo de tránsito' }),
    companiaSoat: z.string().min(1, { message: 'Seleccione una compañía de SOAT' }),
    numeroPolizaSoat: z.string().regex(/^[A-Z0-9]+$/, { message: 'Solo letras mayúsculas y dígitos' }).min(1, { message: 'Ingrese el número de póliza SOAT' }),
    numeroRevisionTecnicoMecanica: z.string().regex(/^[A-Z0-9]+$/, { message: 'Solo letras mayúsculas y dígitos' }).min(1, { message: 'Ingrese el número de revisión técnico mecánica' }),
    fechaExpedicionSoat: z.string().optional().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }),
    fechaVencimientoSoat: z.string().optional().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }),
    fechaVencimientoRevision: z.string().optional().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }),
    gpsUsuario: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).optional()
    ),
    gpsContrasena: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).optional()
    ),
    gpsUrl: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string()
            .regex(
                /^(https?:\/\/)?(www\.)?[-\w@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
                { message: 'URL inválida' }
            )
            .optional()
    ),
    leasingNumeroContrato: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^\d+$/, { message: 'Número de contrato inválido' }).optional()
    ),
    leasingFechaInicio: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }).optional()
    ),
    polizaNitCompania: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).regex(/^\d+(-\d)?$/, { message: 'NIT inválido' }).optional()
    ),
    polizaNumero: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().min(1).optional()
    ),
    polizaFechaVencimiento: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z.string().refine(val => !val || !isNaN(Date.parse(val)), { message: 'Fecha inválida' }).optional()
    ),

    // -------------------------------------------------------------------------
    // Validación de imágenes: requerido, tipo 'File' y tamaño <= 2MB
    // -------------------------------------------------------------------------
    fotoMatriculaFrontal: z.any().superRefine((val, ctx) => {
        if (val === undefined) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Requerido' });
        } else if (!(val instanceof File)) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Archivo inválido' });
        } else if(!val.type.startsWith('image/')) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Archivo inválido' });
        }else if (val.size > MAX_FILE_SIZE) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'La imagen debe pesar menos de 2 MB' });
        }
    }),

    fotoMatriculaPosterior: z.any().superRefine((val, ctx) => {
        if (val === undefined) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Requerido' });
        } else if (!(val instanceof File)) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Archivo inválido' });
        } else if(!val.type.startsWith('image/')) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'Archivo inválido' });
        }else if (val.size > MAX_FILE_SIZE) {
            ctx.addIssue({ code: ZodIssueCode.custom, message: 'La imagen debe pesar menos de 2 MB' });
        }
    }),
});
